import React from "react";

const ZoomIntegrationGuide = () => {
  const updated = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          <div className="card shadow-sm">
            <div className="card-body p-4 p-md-5">

              <header className="mb-4 mb-md-5">
                <h1 className="h2 fw-bold mb-2">Zoom Integration Guide</h1>
                <p className="text-muted mb-0">
                  Last updated: {updated}
                </p>
              </header>

              {/* Overview */}
              <section className="mb-4">
                <h2 className="h5 fw-semibold">Overview</h2>
                <p className="mb-0">
                  Our platform integrates with Zoom to allow users to securely
                  create, manage, and join Zoom meetings directly from our
                  application. This integration uses Zoom’s official OAuth 2.0
                  authorization and Zoom REST APIs. Users must explicitly
                  authorize their Zoom account before any meetings can be
                  created or managed.
                </p>
              </section>

              {/* Connect */}
              <section className="mb-4">
                <h2 className="h5 fw-semibold">Connecting Your Zoom Account</h2>
                <ol className="ps-4 mb-2">
                  <li>Go to the Video Conference section in the application</li>
                  <li>Select Zoom as your provider</li>
                  <li>Click <strong>Connect Zoom</strong></li>
                  <li>Sign in to your Zoom account</li>
                  <li>Approve the requested permissions</li>
                </ol>
                <p className="mb-2">Once approved, your Zoom account will be securely linked.</p>

                <p className="mb-0">You will see a confirmation that your account is connected.</p>
              </section>

              {/* Creating Meetings */}
              <section className="mb-4">
                <h2 className="h5 fw-semibold">Creating Meetings</h2>

                <p className="mb-2">After connecting Zoom:</p>

                <h3 className="h6 fw-semibold mt-3">Instant Meetings</h3>
                <ul className="ps-4 mb-0">
                  <li>Enter a meeting topic</li>
                  <li>Select Instant Meeting</li>
                  <li>Click Start Meeting</li>
                </ul>
                <p className="mb-2">A Zoom meeting will be created immediately and you can join via the provided link.</p>

                <h3 className="h6 fw-semibold mt-3">Scheduled Meetings</h3>
                <ul className="ps-4 mb-0">
                  <li>Enter meeting topic</li>
                  <li>Select Scheduled Meeting</li>
                  <li>Choose date, time, and duration</li>
                  <li>Click Schedule Meeting</li>
                </ul>
                <p className="mb-2">The meeting will be created on Zoom and appear in your upcoming meetings list.</p>
              </section>

              {/* Managing Meetings */}
              <section className="mb-4">
                <h2 className="h5 fw-semibold">Managing Meetings</h2>
                <ul className="ps-4 mb-0">
                  <li>View upcoming meetings</li>
                  <li>Join meetings</li>
                  <li>Copy join links</li>
                  <li>Delete meetings created via the platform</li>
                </ul>
              </section>

              {/* Disconnect */}
              <section className="mb-4">
                <h2 className="h5 fw-semibold">Disconnecting Zoom</h2>
                <p>
                  You can disconnect Zoom anytime by clicking <strong>Logout</strong>
                  {' '}in the Zoom section of the app.
                </p>
                <p className="mb-0">
                  You may also revoke access from your Zoom account under:
                  Zoom App Marketplace → Manage Apps.
                </p>
              </section>

              {/* Permissions */}
              <section className="mb-4">
                <h2 className="h5 fw-semibold">Permissions Requested</h2>
                <p>The app only requests permissions required to manage meetings:</p>
                <ul className="ps-4 mb-0">
                  <li>Create meetings</li>
                  <li>Read meeting details</li>
                  <li>Update meetings</li>
                  <li>Delete meetings</li>
                </ul>
              </section>

              {/* Data Storage */}
              <section className="mb-4">
                <h2 className="h5 fw-semibold">Data Usage & Storage</h2>
                <p>We store only minimal data needed for scheduling:</p>
                <ul className="ps-4">
                  <li>Meeting ID</li>
                  <li>Topic</li>
                  <li>Start time</li>
                  <li>Duration</li>
                  <li>Join URL</li>
                </ul>

                <p className="mb-0">
                  We do <strong>not</strong> store recordings, chat content,
                  passwords, or Zoom credentials.
                </p>
                <p className="mb-0">All communication is secured via HTTPS.</p>
              </section>

              {/* Security */}
              <section className="mb-4">
                <h2 className="h5 fw-semibold">Security</h2>
                <ul className="ps-4 mb-0">
                  <li>OAuth 2.0 secure authorization</li>
                  <li>Server-side token handling</li>
                  <li>HTTPS encrypted communication</li>
                  <li>No storage of Zoom login credentials</li>
                </ul>
              </section>

              {/* Troubleshooting */}
              <section className="mb-4">
                <h2 className="h5 fw-semibold">Troubleshooting</h2>
                <ul className="ps-4 mb-0">
                  <li>Ensure popups are enabled during login</li>
                  <li>Reconnect Zoom if authorization expires</li>
                  <li>Ensure you approve Zoom permissions</li>
                  <li>Refresh the page if meetings don’t appear</li>
                </ul>
              </section>

              {/* Support */}
              <section>
                <h2 className="h5 fw-semibold">Support</h2>
                <p>
                  If you need assistance, contact us:
                </p>
                <ul>
                <li><strong>Email</strong>: <a href="mailto:support@prospectpulse.in">support@prospectpulse.in</a></li>
                <li><strong>Phone number</strong>: <a href="tel:+919345200796">+91 9345200796</a></li>
                <li><strong>Mail</strong>: B-16, Koyla Vihar, Seepat Road, Near RTO Office, Bilaspur, Chhattisgarh-495006</li>
              </ul>
              </section>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZoomIntegrationGuide;
